// PitchPage.jsx — Landing page
// -----------------------------------------------------------------------------
// • React component only – styling is defined in the PitchPage.css section below.
// • Uses classNames (no Tailwind) so you can drop PitchPage.css into your assets.
// -----------------------------------------------------------------------------

import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import '../assets/styles/PitchPage.css';
import '../assets/styles/LandingLayout.css';

const PitchPage = ({ isEmbedded = false }) => {
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile for conditional rendering
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <div className={`approach-container landing-section ${isEmbedded ? 'embedded-pitch' : ''} ${isMobile ? 'mobile-optimized' : ''}`}>
      {/* ───────────────────────────────────────────────────────────────── Hero */}
      {!isEmbedded && (
        <header className="page-header">
          <h1>AI Maths Tutor</h1>
          <p>Your child’s personalised, AI‑powered maths coach – honest, empathic, and curriculum‑aligned.</p>
        </header>
      )}

      {/* 0️⃣  Straight‑Up Pitch */}
      <section className="pitch-hero">
        <h2 className="pitch-headline">Here's the pitch.</h2>
        <div className="pitch-intro">
          <p className="lead-paragraph">
            I've tutored many students in the past, from grades 5 to 12 and across all levels of capabilities. I've tutored students who wanted to <strong>excel and get ahead</strong>, and also students who <strong>just wanted to pass</strong> and have a "good enough" understanding for life. I know the stress maths can cause families, and how <em>freeing</em> it is to actually feel you are on top of your school work and achieving your personal goals.
          </p>
          
          <p className="lead-paragraph">
            The <span className="key-highlight">AI Maths tutor</span> is a textbook-aligned, personalised AI tutor that is <strong>effective</strong>, <strong>affordable</strong>, <strong>available 24/7</strong> and can help students no matter what their goals or level of understanding is. {!isMobile ? (
              <>The AI maths tutor combines <span className="key-highlight">specialised state of the art AI models</span> and development experience, with my <span className="key-highlight">deep understanding of the curriculum</span> and the needs of students from my tutoring background.</>
            ) : (
              <>It combines <span className="key-highlight">AI expertise</span> with <span className="key-highlight">deep curriculum knowledge</span> from my tutoring background.</>
            )}
          </p>
          
          <p className="story-intro">
            {!isMobile ? (
              <>After tutoring my sister after work, I realised I could provide a service in this space that merges my professional experience in AI development and extensive maths tutoring background. <em>Here's what makes AI Maths Tutor different:</em></>
            ) : (
              <><em>Here's what makes AI Maths Tutor different:</em></>
            )}
          </p>
        </div>

        <div className="offering-list">
          <div className="offering-item">
            <div className="offering-number">1</div>
            <div className="offering-content">
              <h3>Textbook Aligned Tutoring Sessions</h3>
              <p>
                {!isMobile ? (
                  <>These are <span className="key-highlight">mapped to the student's textbook and curriculum</span>, so as to align seamlessly with what the student is learning in class <em>(just like a human tutor would)</em>. E.g. a student can select a chapter from their textbook and the AI will provide a session that is aligned to that chapter. Sessions will go over <span className="key-highlight">key outcomes from the chapter</span>, and then provide a series of questions that are aligned to the chapter, and provide an opportunity to narrow down on specific areas or go over <strong>common silly mistakes and gotcha questions</strong>.</>
                ) : (
                  <>Sessions are <span className="key-highlight">mapped to your textbook and curriculum</span> to align with what you're learning in class. Select any chapter and get targeted practice with <span className="key-highlight">key outcomes</span> and <strong>common gotcha questions</strong>.</>
                )}
              </p>
            </div>
          </div>

          <div className="offering-item">
            <div className="offering-number">2</div>
            <div className="offering-content">
              <h3>Varying Levels of Difficulty</h3>
              <p>
                {!isMobile ? (
                  <>The AI can provide sessions that are <span className="key-highlight">aligned to the student's level of understanding</span>, so they can start with the basics and build up to more complex concepts. E.g. a student can select a session that is aligned to their textbook chapter, but also select a level of difficulty <em>(e.g. <strong>"Get the Basics"</strong>, <strong>"Revision"</strong>, and <strong>"Expert"</strong>)</em>. Therefore whether a student feels they didn't understand anything in class, or just want to revise a topic, or are looking to be challenged, the AI can provide a session that is <span className="key-highlight">aligned to their needs</span>.</>
                ) : (
                  <>Choose from <strong>"Get the Basics"</strong>, <strong>"Revision"</strong>, or <strong>"Expert"</strong> modes. Whether you need to start from scratch, review, or be challenged, sessions are <span className="key-highlight">aligned to your needs</span>.</>
                )}
              </p>
            </div>
          </div>

          <div className="offering-item">
            <div className="offering-number">3</div>
            <div className="offering-content">
              <h3>Conversational Tutoring Tone</h3>
              <p>
                The AI uses a <span className="key-highlight">Socratic method of tutoring</span>, which means it asks questions to guide the student to the answer, rather than just giving them the answer. This is a <strong>more effective way of learning</strong> and helps students to develop their <span className="key-highlight">critical thinking skills</span>. The AI also uses an <strong>empathic tone</strong>, which means it can detect when a student is frustrated or bored and adapt its tone and pace accordingly.
              </p>
            </div>
          </div>

          <div className="offering-item">
            <div className="offering-number">4</div>
            <div className="offering-content">
              <h3>Lesson Plan & Study Sheet</h3>
              <p>
                Every lesson comes with a <span className="key-highlight">lesson plan and study sheet</span> that the student can use. The study sheet includes <strong>key ideas</strong>, <strong>example questions</strong> and common <strong>"gotcha" questions</strong> to watch out for. These will be available for the user to view while participating in the session.
              </p>
            </div>
          </div>

          <div className="offering-item">
            <div className="offering-number">5</div>
            <div className="offering-content">
              <h3>Visual and Interactive</h3>
              <p>
                The AI can generate <span className="key-highlight">graphs, diagrams, and other visual aids</span> to help students understand complex concepts. It can also <strong>read out questions and explanations (text to speech)</strong>, which can be helpful for students who are <strong>auditory learners</strong> and struggle to focus on just text.
              </p>
            </div>
          </div>

          <div className="offering-item">
            <div className="offering-number">6</div>
            <div className="offering-content">
              <h3>Homework Help</h3>
              <p>
                The app will also have a <span className="key-highlight">generic page for students</span> to get help with one off homework questions <em>(not aligned to a tutoring session)</em>. Students can <strong>upload/drag and drop screenshots</strong> of their homework questions, and the AI will guide them through the question, <span className="key-highlight">step by step</span>. This is a great way for students to get help with their homework without having to wait for a tutor or teacher to be available.
              </p>
            </div>
          </div>

          <div className="offering-item">
            <div className="offering-number">7</div>
            <div className="offering-content">
              <h3>Affordable</h3>
              <p>
                The AI Maths Tutor is a <span className="key-highlight">fraction of the cost</span> of a human tutor, and is <strong>available 24/7</strong>. This means that students can get help whenever they need it, without having to worry about <strong>scheduling conflicts or high costs</strong>. It will <em>(and should not)</em> ever replace a human tutor, but it is a <strong>great supplement</strong> to tutoring, or even a great compliment to a student who already has a tutor but wants <span className="key-highlight">on demand help</span> outside of their tutoring sessions.
              </p>
            </div>
          </div>

          <div className="offering-item">
            <div className="offering-number">8</div>
            <div className="offering-content">
              <h3>Available 24/7</h3>
              <p>
                The AI Maths Tutor is <span className="key-highlight">available 24/7</span>, which means that students can get help whenever they need it. This is especially helpful for students who have <strong>busy schedules</strong> or who need help outside of regular school hours.
              </p>
            </div>
          </div>

        <div className="offering-item">
            <div className="offering-number">9</div>
            <div className="offering-content">
              <h3>Exam Prep (Coming soon)</h3>
              <p>
                The AI Maths Tutor will also have a <span className="key-highlight">dedicated exam prep mode</span> that will help students prepare for their exams. This will include <strong>practice exams</strong>, <strong>revision sessions</strong>, and <strong>exam strategies</strong>. The exam prep mode will be available in the coming months.
              </p>
            </div>
          </div>
        </div>

        <div className="demo-section">
          <p className="demo-intro"><em>Here's a demo video of the app in action.</em></p>
          
          <div className="video-wrapper">
            {/* Placeholder for when demo video is available */}
            <div className="video-placeholder">
              <div className="placeholder-content">
                <div className="placeholder-icon">🎬</div>
                <h3>Demo Video Coming Soon</h3>
                <p>We're putting the finishing touches on our demo video. In the meantime, check out our user guide to see what AI Maths Tutor can do!</p>
                <a href="/user-guide" target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                  View User Guide
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="cta-buttons">
          <a href="/user-guide" target="_blank" rel="noopener noreferrer" className="btn btn-primary">
            📖 Explore the User Guide
          </a>
          <Link to="/early-access" className="btn btn-secondary">
            🚀 Sign Up for Early Access
          </Link>
        </div>
      </section>

      {/* ①  Why Choose */}
      <section className="pitch-section">
        <h2>Why Choose AI Maths Tutor?</h2>
        <div className="value-grid">
          <div className="value-item">
            <div className="value-icon">🎓</div>
            <h3>Missed a class, "Didn't get it", or don't get enough 1:1 time with your teacher</h3>
            <p>
            Personalised Learning Modes (“Get the Basics”, “Revision”, “Become an Expert”) let you start from scratch or revise what you’ve already learned, or get ahead of the class.
            </p>
          </div>
          <div className="value-item">
            <div className="value-icon">📚</div>
            <h3>Lessons aligned with your specific textbook</h3>
            <p>
              The lessons follow the same structure as your selected textbook when you setup your profile (E.g. Pearson, Cambridge, Oxford, Maths in Focus, or a general syllabus path) so it's easy to compliment what you're learning in class.
            </p>
          </div>
          <div className="value-item">
            <div className="value-icon">🤝</div>
            <h3>Study at your own pace</h3>
            <p>
              Re-take lessons with the AI tutor at different difficulties, visualise concepts, and quiz yourself until you feel confident – no pressure, no judgement.
            </p>
          </div>
          <div className="value-item">
            <div className="value-icon">📈</div>
            <h3>Stuck on homework problems</h3>
            <p>
              In our home help page, upload a screenshot of your homework question and the AI will guide you through it step by step. This is a great way to get help with your homework without having to wait for a tutor or teacher to be available.
            </p>
          </div>
        </div>
      </section>

      {/* ②  How It Works */}
      <section className="pitch-section">
        <h2>How a Tutoring Session Works</h2>
        <div className="tutoring-steps">
          <div className="tutoring-step">
            <div className="step-icon setup-icon">📋</div>
            <div className="step-content">
              <h3>Set Up Your Profile</h3>
              <p>Select your year, textbook and specific details about your learning style. Takes less than a minute to get started.</p>
            </div>
          </div>
          
          <div className="tutoring-step">
            <div className="step-icon topic-icon">📚</div>
            <div className="step-content">
              <h3>Choose Your Topic</h3>
              <p>Pick any chapter from your textbook. For example, select "Chapter 1" then "1A Algebraic Expressions" to match what you're learning in class.</p>
            </div>
          </div>
          
          <div className="tutoring-step">
            <div className="step-icon mode-icon">🎯</div>
            <div className="step-content">
              <h3>Select Your Learning Mode</h3>
              <p>Choose the level that matches your needs and switch anytime:</p>
              <div className="difficulty-showcase">
                <div className="difficulty-option get-basics">
                  <span className="difficulty-badge">Get the Basics</span>
                  <span className="difficulty-description">Step-by-step guidance for fundamental understanding</span>
                </div>
                <div className="difficulty-option revision">
                  <span className="difficulty-badge">Revision</span>
                  <span className="difficulty-description">Practice and reinforce concepts you've learned</span>
                </div>
                <div className="difficulty-option expert">
                  <span className="difficulty-badge">Become an Expert</span>
                  <span className="difficulty-description">Challenge yourself with advanced problems</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="tutoring-step">
            <div className="step-icon chat-icon">💬</div>
            <div className="step-content">
              <h3>Start Learning</h3>
              <p>Chat with your AI tutor, visualize complex concepts, and work through problems at your own pace. Save your progress and jump back in anytime.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ③  Curriculum Snapshot */}
      <section className="pitch-section">
        <h2>Currently Supported Textbooks and Year Groups</h2>
        <div className="curriculum-structure">
          <div className="state-section">
            <h3 className="state-title">NSW</h3>
            <div className="year-levels">
              <div className="year-level">
                <h4 className="year-title">Year 11 Standard</h4>
                <div className="textbooks-list">
                  <div className="textbook-item">Cambridge</div>
                  <div className="textbook-item">General Syllabus</div>
                </div>
              </div>
              <div className="year-level">
                <h4 className="year-title">Year 11 Advanced</h4>
                <div className="textbooks-list">
                  <div className="textbook-item">Nelson (Math in Focus)</div>
                  <div className="textbook-item">General Syllabus</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <p className="beta-note">
          <strong>*</strong> Currently in pre-release beta mode. More support coming soon.
        </p>
      </section>

      {/* ③.5  Pricing */}
      <section className="pitch-section">
        <h2>Pricing</h2>
        <div className="pricing-container">
          <div className="pricing-card">
            <div className="pricing-header">
              <h3>AI Maths Tutor</h3>
              <div className="pricing-badge">Coming Soon</div>
            </div>
            <div className="pricing-content">
              <div className="price-display">
                <span className="price-text">Pricing details coming soon</span>
              </div>
              <p className="pricing-description">
                We're evaluating a price that is fair to cover our operational costs while making AI Maths Tutor as affordable as possible for students and families.
              </p>
              <div className="pricing-features">
                <div className="feature-item">
                  <span className="feature-icon">✓</span>
                  <span>24/7 access to AI tutoring</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">✓</span>
                  <span>Textbook-aligned lessons</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">✓</span>
                  <span>Homework help with image upload</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">✓</span>
                  <span>Interactive visualizations</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">✓</span>
                  <span>Progress tracking</span>
                </div>
              </div>
            </div>
            <div className="pricing-footer">
              <Link to="/early-access" className="btn btn-primary">
                Request Free Early Access
              </Link>
              <p className="early-access-note">
                Join our early access program to be notified when pricing is announced
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ④  CTA */}
      <section className="pitch-section cta">
        <h2>Ready to Get Started?</h2>
        <p>Explore our comprehensive guide to see how AI Maths Tutor works, then join our early access program.</p>
        <div className="cta-buttons">
          <a href="/user-guide" target="_blank" rel="noopener noreferrer" className="btn btn-primary">
            📖 User Guide
          </a>
          <Link to="/early-access" className="btn btn-secondary">
            🎯 Request Free Early Access
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PitchPage;
